import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import styles from './ListaUsuarios.module.css';

function RevisarClientePendente() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { get, post } = useHttpClient();
    const { exibirErro, exibirSucesso } = useNotificacoes();

    const [loading, setLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [clientePendente, setClientePendente] = useState(null);
    const [clientesDestino, setClientesDestino] = useState([]);
    const [ucs, setUcs] = useState([]);
    const [idClienteDestino, setIdClienteDestino] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const usuario = await get(`/hello-sol/usuarios/${id}`);
                setClientePendente(usuario);

                if (!usuario?.cadastroPendente) {
                    exibirErro('Este cadastro não está pendente para revisão.');
                    navigate('/home/listar-usuario');
                    return;
                }

                const [listaClientes, listaUcs] = await Promise.all([
                    get(`/hello-sol/usuarios/buscarUsuarioUsina?idUsina=${usuario.idUsina}&idTipoUsuario=3`),
                    get(`/hello-sol/unidades-consumidoras/buscar?idCliente=${usuario.id}`)
                ]);

                const candidatos = (Array.isArray(listaClientes) ? listaClientes : [])
                    .filter((item) => item.id !== usuario.id)
                    .filter((item) => !item.cadastroPendente);

                setClientesDestino(candidatos);
                setUcs(Array.isArray(listaUcs) ? listaUcs : []);
            } catch (error) {
                exibirErro(error.message || 'Erro ao carregar a revisão do cliente pendente.');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, get, exibirErro, navigate]);

    const totalUc = useMemo(() => ucs.length, [ucs]);

    const handleRevincular = async () => {
        if (!idClienteDestino) {
            exibirErro('Selecione o cliente definitivo.');
            return;
        }

        try {
            setSalvando(true);
            const response = await post(`/hello-sol/usuarios/${id}/revincular`, {
                idClienteDestino: Number(idClienteDestino)
            });
            const totalRevinculado = response?.data?.totalUcRevinculada ?? totalUc;
            exibirSucesso(`Revinculação concluída. ${totalRevinculado} UC(s) transferida(s) para o cliente definitivo.`);
            navigate('/home/listar-usuario');
        } catch (error) {
            exibirErro(error.message || 'Erro ao revincular cliente pendente.');
        } finally {
            setSalvando(false);
        }
    };

    if (loading) {
        return (
            <div className={styles_global.container}>
                <h2>Revisar Cliente Pendente</h2>
                <p className={styles_global.loading}>Carregando...</p>
            </div>
        );
    }

    return (
        <div className={styles_global.container}>
            <h2>Revisar Cliente Pendente</h2>

            <div style={{ marginBottom: '20px', padding: '14px 16px', borderRadius: '8px', background: '#fff3cd', color: '#8a5a00' }}>
                Este cadastro foi criado automaticamente e está pendente de revisão. Você pode regularizar este mesmo cadastro ou revincular as UCs para um cliente definitivo já existente.
            </div>

            <div className={styles.filterForm} style={{ gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'auto auto auto' }}>
                <div className={styles_content.formField}>
                    <label>Usina</label>
                    <input value={clientePendente?.usina?.nome || ''} disabled />
                </div>
                <div className={styles_content.formField}>
                    <label>Origem</label>
                    <input value={clientePendente?.origemCadastro || 'PENDENTE'} disabled />
                </div>
                <div className={styles_content.formField}>
                    <label>Cliente Pendente</label>
                    <input value={clientePendente?.nome || ''} disabled />
                </div>
                <div className={styles_content.formField}>
                    <label>Total de UCs vinculadas</label>
                    <input value={String(totalUc)} disabled />
                </div>
                <div className={styles_content.formField} style={{ gridColumn: '1 / 3' }}>
                    <label>Cliente definitivo de destino</label>
                    <select value={idClienteDestino} onChange={(e) => setIdClienteDestino(e.target.value)}>
                        <option value="">Selecione o cliente definitivo</option>
                        {clientesDestino.map((cliente) => (
                            <option key={cliente.id} value={cliente.id}>
                                {cliente.nome} {cliente.cpfCnpj ? `- ${cliente.cpfCnpj}` : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className={styles.tableContainer} style={{ marginTop: '24px' }}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>CÓD. UC</th>
                        <th>NOME DA UC</th>
                        <th>LOCALIZAÇÃO</th>
                    </tr>
                    </thead>
                    <tbody>
                    {ucs.map((uc) => (
                        <tr key={uc.id}>
                            <td>{uc.codCliente || '—'}</td>
                            <td>{uc.nomeUnidade || '—'}</td>
                            <td>{uc.localizacaoUnidade || '—'}</td>
                        </tr>
                    ))}
                    {ucs.length === 0 && (
                        <tr>
                            <td colSpan={3}>Nenhuma UC ativa vinculada a este cadastro pendente.</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            <div className={styles.botoesAbaixo} style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => navigate(`/home/detalhar-usuario/${id}`)}>
                    Voltar
                </button>
                <button type="button" onClick={() => navigate(`/home/cadastrar-usuario/${id}`)}>
                    Regularizar Mesmo Cadastro
                </button>
                <button type="button" onClick={handleRevincular} disabled={salvando || !idClienteDestino}>
                    {salvando ? 'Revinculando...' : 'Revincular UCs'}
                </button>
            </div>
        </div>
    );
}

export default RevisarClientePendente;

